import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  RefreshCw,
  CheckCircle2,
  XCircle,
  X,
  AlertTriangle,
  Clock,
} from "lucide-react";
import type { JobActivity, ActivityState } from "../lib/syncEvents.js";
import { api } from "../lib/api.js";
import { cn } from "../lib/cn.js";

// Mirror the staleness threshold the SyncProgressBanner uses so both
// surfaces flip to "stuck" at the same moment. Without this the header
// keeps saying "1 sync job running" while the inline banner shows "Stuck
// — no progress in 3 min" — visually contradictory state for the same job.
const STUCK_AFTER_MS = 3 * 60 * 1000;

function isJobStuck(job: JobActivity, now: number): boolean {
  if (job.status === "completed" || job.status === "partial" || job.status === "failed") return false;
  if (job.pagesDone > 0) return false;
  // Pure "row created, no worker has touched it" === waiting in queue,
  // not stuck. lastTickAt comes from the row's lastUpdatedAt; if it
  // equals startedAt the worker hasn't ack'd yet.
  if (job.lastTickAt <= job.startedAt) return false;
  return now - job.lastTickAt > STUCK_AFTER_MS;
}

function isJobWaiting(job: JobActivity): boolean {
  if (job.status === "completed" || job.status === "partial" || job.status === "failed") return false;
  if (job.pagesDone > 0 || (job as JobActivity).pagesTotal === 0) return false;
  // Worker hasn't acked the row yet — message is sitting in SQS behind
  // a backlog. Common when a page-scope sync is enqueued during a
  // running section sync.
  return job.lastTickAt <= job.startedAt;
}

/** Re-render the panel every 30s so the staleness check reflects elapsed
 * time even when no WebSocket frames are arriving. */
function useNowTick(intervalMs: number = 30_000): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

interface ActivityPanelProps {
  state: ActivityState;
}

/**
 * Header strip: collapsed summary of running jobs, expandable into per-job
 * progress rows. Completed jobs auto-dismiss after 5 seconds.
 *
 * Driven by WebSocket `sync_progress` frames, so what we surface here is
 * exactly what the backend tracks in DynamoDB sync_progress: scope label,
 * pages done/total, status, two-phase purge progress for force-resyncs.
 * The earlier per-page-title and per-attachment-filename live state was
 * SSE-only and went away with the migration.
 */
export function ActivityPanel({ state }: ActivityPanelProps) {
  const [expanded, setExpanded] = useState(true);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const all = Object.values(state.jobs);
    for (const j of all) {
      // Auto-dismiss only clean completes. Keep "partial" and "failed"
      // sticky so the user actually notices the error count — they can
      // dismiss explicitly via the × button.
      const autoDismiss = j.status === "completed";
      if (
        autoDismiss &&
        j.finishedAt &&
        !dismissedIds.has(j.jobId) &&
        !timersRef.current.has(j.jobId)
      ) {
        timersRef.current.set(
          j.jobId,
          setTimeout(() => {
            setDismissedIds((prev) => {
              const next = new Set(prev);
              next.add(j.jobId);
              return next;
            });
            timersRef.current.delete(j.jobId);
          }, 5000),
        );
      }
    }
    return () => {
      for (const t of timersRef.current.values()) clearTimeout(t);
      timersRef.current.clear();
    };
  }, [state.jobs]);

  const jobs = useMemo(() => {
    const all = Object.values(state.jobs);
    return all
      .filter((j) => {
        const isTerminal = j.status === "completed" || j.status === "partial" || j.status === "failed";
        return !isTerminal || !dismissedIds.has(j.jobId);
      })
      .sort((a, b) => b.startedAt - a.startedAt);
  }, [state.jobs, dismissedIds]);

  const dismiss = (jobId: string) => {
    const t = timersRef.current.get(jobId);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(jobId);
    }
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(jobId);
      return next;
    });
  };

  const now = useNowTick();

  // Compute live/stuck/waiting BEFORE any early return so hook order stays
  // stable (Rules of Hooks — `useQuery` below must be called every render).
  const live = jobs.filter(
    (j) => j.status === "running" || j.status === "queued" || j.status === "rate_limited",
  );
  const stuck = live.filter((j) => isJobStuck(j, now));
  const waiting = live.filter((j) => isJobWaiting(j));
  const running = live.filter((j) => !isJobStuck(j, now) && !isJobWaiting(j));

  // Poll queue depth only when we have a waiting job to display next to.
  // MUST be called unconditionally (no early-returns above) to satisfy
  // React's Rules of Hooks; `enabled` controls whether it actually fires.
  const queueDepthQ = useQuery({
    queryKey: ["syncQueueDepth"],
    queryFn: api.syncQueueDepth,
    enabled: waiting.length > 0,
    refetchInterval: waiting.length > 0 ? 10_000 : false,
  });

  if (jobs.length === 0) return null;

  // Color/border the panel red when every active job is stuck — that's the
  // user-facing signal that nothing is making progress. Mixed running+stuck
  // stays blue; the per-row UI calls out the stuck ones individually.
  const allStuck = live.length > 0 && stuck.length === live.length;
  const headerColor = allStuck
    ? "border-red-200 bg-red-50 text-red-900 hover:bg-red-100"
    : "border-blue-200 bg-blue-50 text-blue-950 hover:bg-blue-100";
  const wrapperColor = allStuck
    ? "border-red-200 bg-red-50 text-red-900"
    : "border-blue-200 bg-blue-50 text-blue-950";
  const headerLabel =
    running.length === 0 && stuck.length > 0
      ? `${stuck.length} sync job${stuck.length === 1 ? "" : "s"} stuck`
      : running.length > 0
        ? `${running.length} sync job${running.length === 1 ? "" : "s"} running${stuck.length > 0 ? ` (${stuck.length} stuck)` : ""}`
        : `Recent sync activity`;

  return (
    <div className={cn("rounded-md border text-xs", wrapperColor)}>
      <button
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-2 text-left font-medium",
          headerColor,
        )}
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {allStuck ? (
          <AlertTriangle className="h-3 w-3 text-red-700" />
        ) : (
          <RefreshCw
            className={cn(
              "h-3 w-3",
              running.length > 0 && "animate-spin",
              running.length === 0 && "text-emerald-600",
            )}
          />
        )}
        {headerLabel}
      </button>

      {expanded && (
        <div
          className={cn(
            "space-y-2 border-t px-3 py-2",
            allStuck ? "border-red-200" : "border-blue-200",
          )}
        >
          {jobs.map((j) => (
            <JobRow
              key={j.jobId}
              job={j}
              stuck={isJobStuck(j, now)}
              waiting={isJobWaiting(j)}
              queueAhead={
                isJobWaiting(j) && queueDepthQ.data
                  ? queueDepthQ.data.rawFetch.queued + queueDepthQ.data.rawFetch.inFlight
                  : null
              }
              onDismiss={() => dismiss(j.jobId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function JobRow({
  job,
  stuck,
  waiting,
  queueAhead,
  onDismiss,
}: {
  job: JobActivity;
  stuck: boolean;
  waiting: boolean;
  queueAhead: number | null;
  onDismiss: () => void;
}) {
  const purging = job.phase === "purging" && job.purgeTotal > 0;
  const pagesErrored = Math.max(0, job.pagesTotal - job.pagesDone);
  // pagesErrored is derived because JobActivity doesn't carry it as a
  // distinct field; for terminal-but-non-completed states the gap between
  // total and done is the failure count.
  const pct = purging
    ? Math.round((job.purgeDone / job.purgeTotal) * 100)
    : job.pagesTotal > 0
      ? Math.round((job.pagesDone / job.pagesTotal) * 100)
      : 0;
  const elapsedSec = Math.round(
    ((job.finishedAt ?? Date.now()) - job.startedAt) / 1000,
  );
  const isTerminal = job.status === "completed" || job.status === "partial" || job.status === "failed";
  const isActive =
    job.status === "running" || job.status === "queued" || job.status === "rate_limited";

  // Visual priority: stuck > failed > partial > waiting > active.
  const partial = job.status === "partial";
  const failed = job.status === "failed";
  const tone =
    stuck || failed
      ? { border: "border-red-200", bg: "bg-red-50", text: "text-red-700", time: "text-red-500" }
      : partial
        ? { border: "border-amber-200", bg: "bg-amber-50", text: "text-amber-800", time: "text-amber-600" }
        : waiting
          ? { border: "border-slate-200", bg: "bg-slate-50", text: "text-slate-600", time: "text-slate-500" }
          : { border: "border-blue-100", bg: "bg-white", text: "text-blue-700", time: "text-blue-500" };

  const summary = stuck
    ? `Stuck — no progress in 3 min (worker may have crashed)`
    : waiting
      ? queueAhead != null
        ? `Waiting in queue — ~${queueAhead} message${queueAhead === 1 ? "" : "s"} ahead`
        : `Waiting in queue — picking up shortly…`
      : partial
        ? `${job.pagesDone} of ${job.pagesTotal} synced — ${pagesErrored} failed`
        : failed
          ? `Failed — ${pagesErrored} of ${job.pagesTotal} errored`
          : purging
            ? `${job.purgeDone}/${job.purgeTotal} purging`
            : `${job.pagesDone}/${job.pagesTotal} pages`;

  return (
    <div className={cn("rounded border px-2 py-1.5", tone.border, tone.bg)}>
      <div className="flex items-center gap-2">
        <StatusDot status={job.status} stuck={stuck} waiting={waiting} />
        <span className="truncate font-medium">
          {job.scopeLabel ?? `${job.scopeType}:${job.scopeId.slice(0, 12)}`}
        </span>
        <span className={tone.text}>{summary}</span>
        <span className={cn("ml-auto tabular-nums", tone.time)}>{elapsedSec}s</span>
        {(isTerminal || stuck || waiting) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            className={cn(
              "ml-1 rounded p-0.5",
              stuck
                ? "text-red-500 hover:bg-red-100 hover:text-red-800"
                : "text-blue-400 hover:bg-blue-100 hover:text-blue-700",
            )}
            title="Dismiss"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {isActive && !stuck && (purging ? job.purgeTotal > 0 : job.pagesTotal > 0) && (
        <div className="mt-1 flex h-1 overflow-hidden rounded bg-blue-100">
          <div
            className="h-full bg-blue-600 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {job.lastError && (
        <div className="mt-1 truncate text-[11px] text-red-700" title={job.lastError}>
          {job.lastError}
        </div>
      )}
    </div>
  );
}

function StatusDot({
  status,
  stuck = false,
  waiting = false,
}: {
  status: JobActivity["status"];
  stuck?: boolean;
  waiting?: boolean;
}) {
  if (stuck) return <AlertTriangle className="h-3 w-3 shrink-0 text-red-600" />;
  if (waiting) return <Clock className="h-3 w-3 shrink-0 text-slate-500" />;
  if (status === "running" || status === "queued" || status === "rate_limited")
    return <RefreshCw className="h-3 w-3 shrink-0 animate-spin text-blue-600" />;
  if (status === "completed")
    return <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-600" />;
  if (status === "partial")
    return <AlertTriangle className="h-3 w-3 shrink-0 text-amber-600" />;
  return <XCircle className="h-3 w-3 shrink-0 text-red-600" />;
}
